import { useCallback, useEffect, useMemo, useState } from 'react'
import logo from '../assets/alelo-logo.svg'
import { readClaims, SESSION_LOST_ERROR } from './verificationSession'

/**
 * Tela de verificação pendente — destino do redirect da Action
 * `require-verified-contacts`.
 *
 * A Action manda `session_token` (JWT de 600 s) e `state` na query. O token é
 * a credencial das rotas de desbloqueio do BFF; o `state` é o que devolve a
 * pessoa à transação do Auth0 pelo `/continue`.
 *
 * Esta tela não decide nada: quem libera o acesso é a Action, relendo o
 * usuário na Management API quando a volta acontece.
 */

const API_BASE = (import.meta.env.VITE_ACCOUNT_API_URL || '').replace(/\/$/, '')
const AUTH0_DOMAIN = import.meta.env.VITE_AUTH0_DOMAIN as string
const RESEND_SECONDS = 60

const ERRORS: Record<string, string> = {
  resend_throttled: 'Você pediu um envio há pouco. Aguarde um minuto e tente de novo.',
  invalid_code: 'Código inválido. Confira e tente de novo.',
  expired: 'O código expirou. Peça um novo código.',
  not_found: 'O código expirou. Peça um novo código.',
  too_many_attempts: 'Muitas tentativas. Peça um novo código para continuar.',
  invalid_phone: 'Não encontramos um celular na sua conta. Fale com o atendimento.',
  phone_already_registered: 'Este celular já está em uso por outra conta.',
  sms_send_failed: 'Não foi possível enviar o SMS. Tente novamente.',
  email_send_failed: 'Não foi possível enviar o e-mail. Tente novamente.',
  network_error: 'Não foi possível falar com o servidor. Tente de novo em instantes.',
}

function describe(code: string): string {
  return ERRORS[code] || 'Não foi possível concluir. Tente de novo em instantes.'
}

export function VerificationPendingPage() {
  const params = useMemo(() => new URLSearchParams(window.location.search), [])
  const token = params.get('session_token') || ''
  const state = params.get('state') || ''
  const claims = useMemo(() => (token ? readClaims(token) : null), [token])

  // O e-mail é confirmado fora daqui (no link), então a tela nunca sabe que
  // virou verificado — por isso o botão de continuar fica sempre disponível e
  // quem revalida é a Action, relendo o usuário na Management API.
  const emailVerified = Boolean(claims?.emailVerified)
  const [emailSent, setEmailSent] = useState(false)
  const [resendSeconds, setResendSeconds] = useState(0)
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string>()
  /** O BFF recusou a credencial: a sessão acabou enquanto a tela estava aberta. */
  const [sessionLost, setSessionLost] = useState(false)

  useEffect(() => {
    if (resendSeconds <= 0) return
    const timer = window.setTimeout(() => setResendSeconds(resendSeconds - 1), 1000)
    return () => window.clearTimeout(timer)
  }, [resendSeconds])

  const call = useCallback(
    async (path: string, body?: unknown) => {
      setError(undefined)
      setPending(true)
      try {
        const response = await fetch(`${API_BASE}/api/account/verification/${path}`, {
          method: 'POST',
          headers: {
            authorization: `Bearer ${token}`,
            'content-type': 'application/json',
          },
          body: JSON.stringify(body ?? {}),
        })
        const data = (await response.json().catch(() => ({}))) as {
          ok?: boolean
          error?: string
        }
        if (!response.ok) {
          // Motivo único do BFF para "a credencial morreu", não a ação.
          if (data.error === SESSION_LOST_ERROR) {
            setSessionLost(true)
            return null
          }
          setError(describe(data.error || `http_${response.status}`))
          return null
        }
        return data
      } catch (err) {
        console.error('[verificacao-pendente]', path, err)
        setError(describe('network_error'))
        return null
      } finally {
        setPending(false)
      }
    },
    [token],
  )

  async function onResendEmail() {
    if (await call('resend-email')) {
      setEmailSent(true)
      setResendSeconds(RESEND_SECONDS)
    }
  }

  if (!token || !claims || sessionLost) {
    return (
      <main className="auth-page">
        <div className="auth-card">
          <img src={logo} alt="Alelo" className="auth-logo" />
          <h1 className="auth-title">Sessão expirada</h1>
          <p className="auth-subtitle">
            O tempo para concluir as verificações acabou. Entre de novo para
            continuar de onde parou.
          </p>
          <div className="auth-footer">
            <a href={`https://${AUTH0_DOMAIN}/`}>Voltar para o login</a>
          </div>
        </div>
      </main>
    )
  }

  return (
    <main className="auth-page">
      <div className="auth-card">
        <img src={logo} alt="Alelo" className="auth-logo" />
        <h1 className="auth-title">Falta pouco para liberar seu acesso</h1>
        <p className="auth-subtitle">
          Para sua segurança, confirmamos seu e-mail antes de liberar a
          entrada.
        </p>

        <section className="auth-section">
          <h2>E-mail {emailVerified ? '✅' : '⏳'}</h2>
          <p>{claims.email || 'seu e-mail'}</p>
          {emailVerified ? (
            <p>Confirmado.</p>
          ) : (
            <>
              <p>
                {emailSent
                  ? 'Enviamos um link de verificação. Abra o e-mail e clique no link.'
                  : 'Enviamos um link quando você criou a conta. Não achou? Peça outro.'}
              </p>
              <button
                type="button"
                className="auth-secondary"
                onClick={() => void onResendEmail()}
                disabled={pending || resendSeconds > 0}
              >
                {resendSeconds > 0
                  ? `Reenviar link (${resendSeconds}s)`
                  : 'Reenviar link de verificação'}
              </button>
            </>
          )}
        </section>

        {error ? (
          <p className="auth-alert" role="alert">
            {error}
          </p>
        ) : null}

        <div className="auth-footer">
          <p className="auth-subtitle">
            {emailVerified
              ? 'Tudo confirmado. Você já pode entrar.'
              : 'Já concluiu a verificação? Continue — vamos conferir.'}
          </p>
          <button
            type="button"
            className="auth-primary"
            disabled={!state}
            onClick={() => {
              window.location.assign(
                `https://${AUTH0_DOMAIN}/continue?state=${encodeURIComponent(state)}`,
              )
            }}
          >
            Continuar para minha conta
          </button>
          {state ? null : (
            <p className="auth-alert" role="alert">
              Não recebemos o retorno do login. Entre de novo para continuar.
            </p>
          )}
        </div>
      </div>
    </main>
  )
}
